import type React from "react";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import commonHeaderStyles from "~/components/Document/commonDocumentHeader/index.module.scss";
import Button from "~/components/ui/button";
import { CommonButton } from "~/components/ui/commonButton";
import { TutorialTooltip } from "~/components/ui/tutorialTooltip";
import { sampleSummaryContent, sampleSummaryDocument } from "~/data/sampleProject";
import { useTutorialAccessGuard } from "~/hooks/useTutorialAccessGuard";
import EditWhite from "~/images/edit_white.svg?react";
import ExportPrimary from "~/images/export_primary.svg?react";
import styles from "./index.module.scss";

const sampleTranscription = `Bさん： Aさん、本日はお時間をいただきありがとうございます。本日は先日お話しいただいた、kintoneを活用した営業支援システムの件ですね。

Aさん： はい、よろしくお願いします。早速ですが、我々がどのような管理を実現したいか、改めてお伝えさせてください。現状、顧客情報や進行中の案件、日々の営業活動がExcelや個人の手帳でバラバラに管理されていて、全体像が全く見えない状態なんです。誰がどの顧客に、どんな案件で、いつ、何をしたのかを一元的に可視化したいと考えています。

Bさん： なるほど、承知いたしました。情報の属人化を防ぎ、チーム全体で進捗を共有できる体制を作りたいということですね。そのご要望でしたら、kintoneで3つのアプリを連携させる構成がシンプルで分かりやすいかと思います。

Aさん： 3つのアプリですか。

Bさん： はい。まず、すべての情報の基盤となる「顧客管理」アプリを作成します。ここには、会社名、部署、担当者名、連絡先といった基本的な顧客情報を登録します。いわば、顧客マスタですね。

Aさん： なるほど、それは分かりやすいです。まずはお客様の情報を登録する場所ですね。

Bさん： その通りです。次に、具体的な商談を管理するための「案件管理」アプリを作成します。ここでは、案件名、商談の進捗状況（例：アポ、提案、クロージングなど）、受注予定日、金額といった情報を管理します。

Aさん： ほうほう。

Bさん： そしてここが重要なのですが、この「案件管理」アプリは、先ほどの「顧客管理」アプリと紐づけます。案件を登録する際に、「どの顧客の案件か」を顧客管理アプリから選択して登録する形です。こうすることで、顧客情報と案件情報がシステム上でしっかりと結びつきます。

Aさん： なるほど！Excelだと会社名を手入力するので、表記ゆれが起きたりして集計が大変でしたが、マスタから選ぶ形ならその心配もないですね。顧客コードのようなもので紐づけるイメージでしょうか？

Bさん： はい、その通りです。kintoneではルックアップという機能で簡単に実現できます。そして3つ目が、日々の営業活動を記録する「行動管理」アプリです。

Aさん： 行動管理。日報のようなものでしょうか。

Bさん： そうですね。訪問や電話、メールといった活動内容と、その結果を記録していきます。そして、この「行動管理」アプリも、先ほどの「案件管理」アプリと紐づけます。つまり、「どの案件に対する行動だったのか」を明確にするわけです。

Aさん： なるほど。そうすれば、ある案件に対して、これまでどんなアプローチをしてきたかが時系列で分かりますね。

Bさん： 左様でございます。さらに、この「行動管理」アプリは、「顧客管理」とも直接紐づけておくことをお勧めします。

Aさん： あれ？案件に紐づいていれば、どの顧客かは自動的に分かりませんか？

Bさん： 良いご指摘です。もちろん分かるのですが、例えば「A社に対して、現在進行中の案件とは関係なく、情報提供の電話をした」といったケースもあるかと思います。そういった場合でも記録を残せるように、顧客に直接紐づけられると、より活動履歴の網羅性が高まります。案件コードと顧客コード、両方で紐づけておくと、データの活用がしやすくなります。

Aさん： なるほど、確かにそうですね。よく分かりました。まとめると、「顧客」がいて、そこに「案件」がぶら下がり、さらにその案件に対して「行動」が記録されていく。そして行動は顧客にも直接紐づく、という構造ですね。非常にイメージが湧きました。

Bさん： はい、ご理解いただきありがとうございます。この3つのアプリを連携させることで、Aさんが当初仰っていた「誰が、どの顧客の、どの案件で、何をしたか」という情報の流れ`;

const TutorialSummary: React.FC = () => {
  useTutorialAccessGuard();

  const summary = sampleSummaryDocument;
  const summaryContent = sampleSummaryContent;
  const [showTranscription, setShowTranscription] = useState(false);

  const renderMarkdown = (content: string): React.ReactNode => {
    const processedContent = content.replace(/\\n/g, "\n").replace(/\\"/g, '"');

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => <h1 className={styles["markdownH1"]}>{children}</h1>,
          h2: ({ children }) => <h2 className={styles["markdownH2"]}>{children}</h2>,
          h3: ({ children }) => <h3 className={styles["markdownH3"]}>{children}</h3>,
          p: ({ children }) => <p className={styles["markdownP"]}>{children}</p>,
          ul: ({ children }) => <ul className={styles["markdownUl"]}>{children}</ul>,
          ol: ({ children }) => <ol className={styles["markdownOl"]}>{children}</ol>,
          li: ({ children }) => <li className={styles["markdownLi"]}>{children}</li>,
          code: ({ children, className, ...props }) => {
            const isInline = !className || !className.includes("language-");
            return isInline ? (
              <code className={styles["markdownInlineCode"]} {...props}>
                {children}
              </code>
            ) : (
              <code className={styles["markdownCodeBlock"]} {...props}>
                {children}
              </code>
            );
          },
          pre: ({ children }) => <pre className={styles["markdownPre"]}>{children}</pre>,
          blockquote: ({ children }) => (
            <blockquote className={styles["markdownBlockquote"]}>{children}</blockquote>
          ),
          table: ({ children }) => <table className={styles["markdownTable"]}>{children}</table>,
          thead: ({ children }) => <thead className={styles["markdownThead"]}>{children}</thead>,
          tbody: ({ children }) => <tbody className={styles["markdownTbody"]}>{children}</tbody>,
          tr: ({ children }) => <tr className={styles["markdownTr"]}>{children}</tr>,
          th: ({ children }) => <th className={styles["markdownTh"]}>{children}</th>,
          td: ({ children }) => <td className={styles["markdownTd"]}>{children}</td>,
          a: ({ children, href, ...props }) => (
            <a
              className={styles["markdownLink"]}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {children}
            </a>
          ),
          strong: ({ children }) => (
            <strong className={styles["markdownStrong"]}>{children}</strong>
          ),
          em: ({ children }) => <em className={styles["markdownEm"]}>{children}</em>,
        }}
      >
        {processedContent}
      </ReactMarkdown>
    );
  };

  const handleToggleTranscription = () => {
    setShowTranscription((prev) => !prev);
  };

  return (
    <div className={styles["wrapper"]} data-tour-id="summary-content">
      <div className={commonHeaderStyles["commonDocumentHeader"]}>
        <div className={commonHeaderStyles["commonDocumentHeader-inner"]}>
          <div className={commonHeaderStyles["commonDocumentHeader-left"]}>
            <h1>{summary.name}</h1>
          </div>
          <div className={commonHeaderStyles["commonDocumentHeader-right"]}>
            <div className={commonHeaderStyles["commonDocumentHeader-buttons"]}>
              <TutorialTooltip message="生成されたドキュメントを編集できます" position="bottom">
                <CommonButton icon={<EditWhite />} onClick={() => {}}>
                  編集
                </CommonButton>
              </TutorialTooltip>
              <TutorialTooltip message="さまざまな形式でエクスポートできます" position="bottom">
                <CommonButton icon={<ExportPrimary />} variant="icon" onClick={() => {}} />
              </TutorialTooltip>
            </div>
          </div>
        </div>
      </div>

      <div className={styles["actionBar"]}>
        <Button
          styleType="transcription"
          label={showTranscription ? "文字起こしを閉じる" : "文字起こしを見る"}
          onClick={handleToggleTranscription}
          icon={"transcriptionIcon"}
        />
      </div>

      <div className={styles["contentLayout"]}>
        <div className={styles["markdownWrapper"]}>
          <div className={styles["markdownPreview"]}>{renderMarkdown(summaryContent)}</div>
        </div>

        {showTranscription && (
          <aside className={styles["transcriptionPanel"]}>
            <div className={styles["transcriptionHeader"]}>
              <h3>文字起こし</h3>
              <button
                type="button"
                className={styles["transcriptionClose"]}
                onClick={handleToggleTranscription}
                aria-label="文字起こしを閉じる"
              />
            </div>
            <pre className={styles["transcriptionBody"]}>{sampleTranscription}</pre>
          </aside>
        )}
      </div>
    </div>
  );
};

export default TutorialSummary;
